import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createContext, useContextSelector } from "use-context-selector";
import { useUser } from "../UserContext/UserProvider";
import {
  getNotifications,
  getNotificationCounts,
  markNotificationsRead,
  markNotificationsSeen,
  dismissNotification as dismissNotificationApi,
  getStreamToken,
  getStreamUrl,
} from "../../api/notifications";

/**
 * Global app-wide notifications state.
 *
 * This provider is the ONLY owner of notification fetching, the SSE stream,
 * cache updates, and read/seen mutations. Pages read counts and call actions;
 * they never poll notification endpoints themselves.
 */
const NotificationsContext = createContext(null);

const EMPTY_COUNTS = { total: 0, scopes: {}, entities: {} };
const RECONNECT_BASE_DELAY_MS = 2000;
const RECONNECT_MAX_DELAY_MS = 60000;

const decrementUnreadCounts = (counts, unreadItems = []) => {
  if (!unreadItems.length) return counts;
  const scopes = { ...counts.scopes };
  const entities = { ...(counts.entities || {}) };
  let total = counts.total;
  for (const item of unreadItems) {
    if (total > 0) total -= 1;
    if (item.scope && scopes[item.scope]) {
      const next = scopes[item.scope] - 1;
      if (next > 0) scopes[item.scope] = next;
      else delete scopes[item.scope];
    }
    const entityId = item.target?.entityId;
    if (entityId && entities[entityId]) {
      const next = entities[entityId] - 1;
      if (next > 0) entities[entityId] = next;
      else delete entities[entityId];
    }
  }
  return { total, scopes, entities };
};

export const NotificationsProvider = ({ children }) => {
  const { user } = useUser();

  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedLoaded, setFeedLoaded] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activity, setActivity] = useState({
    version: 0,
    event: null,
    notification: null,
    payload: null,
  });

  const bumpActivity = useCallback((event, payload = null) => {
    setActivity((prev) => ({
      version: prev.version + 1,
      event,
      notification: event === "notification.created" ? payload : null,
      payload,
    }));
  }, []);

  const sourceRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const connectingRef = useRef(false);
  const feedLoadedRef = useRef(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const enabled = Boolean(user) && user?.settings?.inAppNotifications !== false;

  const refreshCounts = useCallback(async () => {
    try {
      const fresh = await getNotificationCounts();
      setCounts(fresh || EMPTY_COUNTS);
    } catch {
      // stream/reconnect will retry; never surface count errors to the user
    }
  }, []);

  const loadFeed = useCallback(
    async ({ reset = false } = {}) => {
      if (feedLoading) return;
      setFeedLoading(true);
      try {
        const cursor = reset ? null : nextCursor;
        const data = await getNotifications({ cursor });
        setItems((prev) =>
          reset || !cursor ? data.items : [...prev, ...data.items]
        );
        setNextCursor(data.nextCursor);
        setHasMore(data.hasMore);
        setFeedLoaded(true);
        feedLoadedRef.current = true;
      } catch {
        // drawer shows a retry state through hasMore/loading
      } finally {
        setFeedLoading(false);
      }
    },
    [feedLoading, nextCursor]
  );

  // ── SSE stream lifecycle ────────────────────────────────────────────────

  const disconnectStream = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }
    connectingRef.current = false;
  }, []);

  const connectStream = useCallback(async () => {
    if (connectingRef.current || sourceRef.current) return;
    connectingRef.current = true;

    let token;
    try {
      token = await getStreamToken();
    } catch {
      connectingRef.current = false;
      scheduleReconnect();
      return;
    }

    const source = new EventSource(getStreamUrl(token));
    sourceRef.current = source;

    source.onopen = () => {
      connectingRef.current = false;
      reconnectAttemptsRef.current = 0;
      // reconcile anything missed while disconnected
      refreshCounts();
      if (feedLoadedRef.current) loadFeed({ reset: true });
    };

    source.addEventListener("notification.created", (e) => {
      try {
        const notification = JSON.parse(e.data);
        setItems((prev) => {
          if (!feedLoadedRef.current) return prev;
          // batch rollups reuse the same _id — replace instead of duplicating
          const without = prev.filter(
            (item) => String(item._id) !== String(notification._id)
          );
          return [notification, ...without];
        });
        bumpActivity("notification.created", notification);
      } catch {}
    });

    source.addEventListener("notification.countsChanged", (e) => {
      try {
        setCounts(JSON.parse(e.data) || EMPTY_COUNTS);
      } catch {}
    });

    // another device read/dismissed items — mirror it locally
    source.addEventListener("notification.updated", (e) => {
      try {
        const payload = JSON.parse(e.data);
        const { ids, scope, entityId, readAt, dismissed } = payload;
        setItems((prev) =>
          prev
            .filter(
              (item) =>
                !(dismissed && ids?.some((id) => String(id) === String(item._id)))
            )
            .map((item) => {
              const matchesId = ids?.some(
                (id) => String(id) === String(item._id)
              );
              const matchesScope = scope && item.scope === scope;
              const matchesEntity =
                entityId && item.target?.entityId === entityId;
              const matchesAll = !ids && !scope && !entityId;
              if (
                readAt &&
                (matchesId || matchesScope || matchesEntity || matchesAll)
              ) {
                return { ...item, readAt: item.readAt || readAt };
              }
              return item;
            })
        );
        bumpActivity("notification.updated", payload);
      } catch {}
    });

    source.addEventListener("sync.required", () => {
      refreshCounts();
      if (feedLoadedRef.current) loadFeed({ reset: true });
      bumpActivity("sync.required", null);
    });

    source.onerror = () => {
      // stream tokens are short-lived, so the built-in EventSource retry would
      // reconnect with an expired token — reconnect manually with a fresh one
      source.close();
      if (sourceRef.current === source) sourceRef.current = null;
      connectingRef.current = false;
      scheduleReconnect();
    };

    function scheduleReconnect() {
      if (reconnectTimerRef.current) return;
      const attempt = reconnectAttemptsRef.current++;
      const delay = Math.min(
        RECONNECT_BASE_DELAY_MS * 2 ** attempt,
        RECONNECT_MAX_DELAY_MS
      );
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connectStream();
      }, delay);
    }
  }, [refreshCounts, loadFeed, bumpActivity]);

  // connect/disconnect with auth + preference changes
  useEffect(() => {
    if (!enabled) {
      disconnectStream();
      setCounts(EMPTY_COUNTS);
      setItems([]);
      setNextCursor(null);
      setHasMore(false);
      setFeedLoaded(false);
      feedLoadedRef.current = false;
      setActivity({ version: 0, event: null, notification: null, payload: null });
      return;
    }

    refreshCounts(); // fetch counts exactly once after auth
    connectStream();

    return () => disconnectStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // reconcile when the tab comes back to the foreground
  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      refreshCounts();
      if (!sourceRef.current && !connectingRef.current) {
        reconnectAttemptsRef.current = 0;
        connectStream();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [enabled, refreshCounts, connectStream]);

  // ── actions ─────────────────────────────────────────────────────────────

  const markRead = useCallback(
    async (ids, knownItems = []) => {
      const idSet = new Set((ids || []).map(String));
      const now = new Date().toISOString();
      const unreadMatches = itemsRef.current.filter(
        (item) => idSet.has(String(item._id)) && !item.readAt
      );
      const extraKnown = (knownItems || []).filter(
        (item) =>
          idSet.has(String(item._id)) &&
          !item.readAt &&
          !unreadMatches.some((match) => String(match._id) === String(item._id))
      );
      const unread = [...unreadMatches, ...extraKnown];
      itemsRef.current = itemsRef.current.map((item) =>
        idSet.has(String(item._id)) && !item.readAt
          ? { ...item, readAt: now }
          : item
      );
      setItems(itemsRef.current);
      if (unread.length > 0) {
        setCounts((prev) => decrementUnreadCounts(prev, unread));
      }
      try {
        await markNotificationsRead({ ids });
      } catch {
        refreshCounts();
      }
    },
    [refreshCounts]
  );

  const markScopeRead = useCallback(
    async (scope) => {
      if (!scope) return;
      const now = new Date().toISOString();
      const unreadMatches = itemsRef.current.filter(
        (item) => item.scope === scope && !item.readAt
      );
      itemsRef.current = itemsRef.current.map((item) =>
        item.scope === scope && !item.readAt ? { ...item, readAt: now } : item
      );
      setItems(itemsRef.current);
      if (unreadMatches.length > 0) {
        setCounts((prev) => decrementUnreadCounts(prev, unreadMatches));
      } else {
        // feed may not be loaded yet — still clear this scope's badge immediately
        setCounts((prev) => {
          if (!prev.scopes?.[scope]) return prev;
          const scopes = { ...prev.scopes };
          const removed = scopes[scope] || 0;
          delete scopes[scope];
          return {
            total: Math.max(0, prev.total - removed),
            scopes,
            entities: prev.entities || {},
          };
        });
      }
      try {
        await markNotificationsRead({ scope });
      } catch {
        refreshCounts();
      }
    },
    [refreshCounts]
  );

  const markEntityRead = useCallback(
    async (entityId) => {
      if (!entityId) return;
      const now = new Date().toISOString();
      const unreadMatches = itemsRef.current.filter(
        (item) => item.target?.entityId === entityId && !item.readAt
      );
      itemsRef.current = itemsRef.current.map((item) =>
        item.target?.entityId === entityId && !item.readAt
          ? { ...item, readAt: now }
          : item
      );
      setItems(itemsRef.current);
      if (unreadMatches.length > 0) {
        setCounts((prev) => decrementUnreadCounts(prev, unreadMatches));
      } else {
        setCounts((prev) => {
          if (!prev.entities?.[entityId]) return prev;
          const entities = { ...prev.entities };
          const removed = entities[entityId] || 0;
          delete entities[entityId];
          return {
            total: Math.max(0, prev.total - removed),
            scopes: prev.scopes || {},
            entities,
          };
        });
      }
      try {
        await markNotificationsRead({ entityId });
      } catch {
        refreshCounts();
      }
    },
    [refreshCounts]
  );

  const markAllRead = useCallback(async () => {
    const now = new Date().toISOString();
    itemsRef.current = itemsRef.current.map((item) =>
      item.readAt ? item : { ...item, readAt: now }
    );
    setItems(itemsRef.current);
    setCounts(EMPTY_COUNTS);
    try {
      await markNotificationsRead({});
    } catch {
      refreshCounts();
    }
  }, [refreshCounts]);

  const dismissNotification = useCallback(
    async (id) => {
      const dismissed = itemsRef.current.find(
        (item) => String(item._id) === String(id)
      );
      itemsRef.current = itemsRef.current.filter(
        (item) => String(item._id) !== String(id)
      );
      setItems(itemsRef.current);
      if (dismissed && !dismissed.readAt) {
        setCounts((prev) => decrementUnreadCounts(prev, [dismissed]));
      }
      try {
        await dismissNotificationApi(id);
      } catch {
        refreshCounts();
      }
    },
    [refreshCounts]
  );

  /** mark read + navigate to the notification target */
  const openNotification = useCallback(
    (notification) => {
      if (!notification) return;
      if (!notification.readAt) markRead([notification._id], [notification]);
      setDrawerOpen(false);
      const route = notification.target?.route;
      if (route) {
        // provider mounts outside the router — hash navigation works everywhere
        window.location.hash = route.startsWith("#")
          ? route.slice(1)
          : route;
      }
    },
    [markRead]
  );

  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
    loadFeed({ reset: true });
    // exposing the list marks everything seen (not read)
    markNotificationsSeen({}).catch(() => {});
  }, [loadFeed]);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const contextValue = useMemo(
    () => ({
      enabled,
      counts,
      items,
      hasMore,
      feedLoading,
      feedLoaded,
      drawerOpen,
      activity,
      refreshCounts,
      loadFeed,
      markRead,
      markScopeRead,
      markEntityRead,
      markAllRead,
      dismissNotification,
      openNotification,
      openDrawer,
      closeDrawer,
    }),
    [
      enabled,
      counts,
      items,
      hasMore,
      feedLoading,
      feedLoaded,
      drawerOpen,
      activity,
      refreshCounts,
      loadFeed,
      markRead,
      markScopeRead,
      markEntityRead,
      markAllRead,
      dismissNotification,
      openNotification,
      openDrawer,
      closeDrawer,
    ]
  );

  return (
    <NotificationsContext.Provider value={contextValue}>
      {children}
    </NotificationsContext.Provider>
  );
};

// ── selector hooks: consumers re-render only for the slices they read ──────

export const useNotifications = () => {
  const context = useContextSelector(NotificationsContext, (state) => state);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationsProvider"
    );
  }
  return context;
};

/** total unread count — for the global bell */
export const useUnreadCount = () =>
  useContextSelector(NotificationsContext, (state) => state?.counts?.total || 0);

/** unread count for one scope — for section badges */
export const useScopeUnreadCount = (scope) =>
  useContextSelector(
    NotificationsContext,
    (state) => state?.counts?.scopes?.[scope] || 0
  );

/** unread count for one target entity — for roster/event card badges */
export const useEntityUnreadCount = (entityId) =>
  useContextSelector(
    NotificationsContext,
    (state) => (entityId ? state?.counts?.entities?.[entityId] || 0 : 0)
  );

/** summed unread count across several scopes — for grouped section badges */
export const useScopesUnreadCount = (scopes = []) =>
  useContextSelector(NotificationsContext, (state) =>
    scopes.reduce(
      (sum, scope) => sum + (state?.counts?.scopes?.[scope] || 0),
      0
    )
  );

export const useNotificationActivity = () =>
  useContextSelector(NotificationsContext, (state) => state?.activity);

export const useNotificationActions = () => {
  const markRead = useContextSelector(
    NotificationsContext,
    (state) => state?.markRead
  );
  const markScopeRead = useContextSelector(
    NotificationsContext,
    (state) => state?.markScopeRead
  );
  const markEntityRead = useContextSelector(
    NotificationsContext,
    (state) => state?.markEntityRead
  );
  const openNotification = useContextSelector(
    NotificationsContext,
    (state) => state?.openNotification
  );
  const openDrawer = useContextSelector(
    NotificationsContext,
    (state) => state?.openDrawer
  );
  const closeDrawer = useContextSelector(
    NotificationsContext,
    (state) => state?.closeDrawer
  );
  return { markRead, markScopeRead, markEntityRead, openNotification, openDrawer, closeDrawer };
};
